import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Button, Typography } from '@mui/material';
import type { SocialPlatform } from '@realadvisor/constants';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { ErrorOption, FieldPath } from 'react-hook-form';

import {
  type FormFieldDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import MarkdownParser from '@modules/_shared/components/MarkdownParser';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import {
  EDIT_MODAL_CONFIGS,
  type GenericEditModalType,
  SOCIAL_PLATFORMS,
  getSocialMediaFieldName,
} from '@modules/_shared/components/visibility-drawer/edit-modals/editModalConfig';
import type { VisibilityEntity } from '@modules/_shared/components/visibility-drawer/types';
import {
  getSlugConstraintError,
  isAgencyEntity,
} from '@modules/_shared/components/visibility-drawer/types';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatPhoneNumberE164 } from '@modules/_shared/utils/phone';
import {
  validateSocialMediaUrl,
  validateWebsiteUrl,
} from '@modules/_shared/utils/urlValidation';
import {
  UPDATE_TEAM_PRIMARY_PHONE_NUMBER,
  UPDATE_TEAM_SETTINGS,
} from '@modules/teams/teamQueries';
import { UPDATE_USER_SETTINGS } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const normalizeUserField = (name: string, value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  if (name === 'description') {
    return value.replace(/\n{2,}/g, '\n\n').trim();
  }
  if (name === 'agent_slug') {
    return value.trim() || null;
  }
  return value;
};

type EntityFieldEditModalProps = {
  type: GenericEditModalType | null;
  open: boolean;
  onClose: () => void;
  entity: VisibilityEntity;
};

const EntityFieldEditModal = ({
  type,
  open,
  onClose,
  entity,
}: EntityFieldEditModalProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isTeam = isAgencyEntity(entity);

  const handleUserError = (error: ErrorLike) => {
    const message = getSlugConstraintError(error, t);
    if (message) {
      setErrorMessage(message);
      return;
    }
    setApolloError(error);
  };

  const [updateUser, { loading: userLoading }] = useMutation(
    UPDATE_USER_SETTINGS,
    {
      onError: handleUserError,
      refetchQueries: ['GetTeamMemberEditDetails'],
      awaitRefetchQueries: true,
    },
  );

  const [updateTeam, { loading: teamLoading }] = useMutation(
    UPDATE_TEAM_SETTINGS,
    {
      onError: setApolloError,
    },
  );

  const [updateTeamPhone, { loading: phoneLoading }] = useMutation(
    UPDATE_TEAM_PRIMARY_PHONE_NUMBER,
    {
      onError: setApolloError,
      refetchQueries: ['GetAgencyEditDetails', 'getTeamSettings'],
      awaitRefetchQueries: true,
    },
  );

  const loading = userLoading || teamLoading || phoneLoading;

  const { config, formDefinition, infoFields, hasOnlyInfoFields } =
    useMemo(() => {
      const configs = EDIT_MODAL_CONFIGS(t, isTeam, isAdmin);
      const config = type ? configs[type] : null;

      const formDefinition = (config?.fields?.filter(
        field => field.type !== 'info',
      ) ?? []) as FormFieldDefinitionType<Record<string, any>>[];

      const infoFields =
        config?.fields?.filter(field => field.type === 'info') ?? [];

      const hasOnlyInfoFields =
        (config?.fields ?? []).length > 0 &&
        config?.fields.every(field => field.type === 'info');

      return {
        config,
        formDefinition,
        infoFields,
        hasOnlyInfoFields,
      };
    }, [type, t, isTeam, isAdmin]);

  const defaultValues = useMemo(() => {
    if (!config || !type) {
      return {};
    }

    if (type === 'teamPhone' && isTeam) {
      return { phone_number: entity.phone_numbers?.[0]?.number ?? '' };
    }

    return formDefinition.reduce((acc, field) => {
      if (field.type === 'address' && isTeam) {
        return {
          ...acc,
          street_number: entity.street_number ?? '',
          route: entity.route ?? '',
          postcode: entity.postcode ?? '',
          locality: entity.locality ?? '',
          state: entity.state ?? '',
          country: entity.country ?? '',
          lat: entity.lat ?? null,
          lng: entity.lng ?? null,
        };
      }

      if ('name' in field) {
        const fieldName = field.name as keyof VisibilityEntity;
        acc[field.name] = entity[fieldName] ?? null;
      }
      return acc;
    }, {} as Record<string, any>);
  }, [config, type, isTeam, formDefinition, entity]);

  const validateForm = (data: Record<string, any>) => {
    const errors: [FieldPath<Record<string, any>>, ErrorOption][] = [];

    if (!type) {
      return errors;
    }

    const socialMediaFieldMap = Object.fromEntries(
      SOCIAL_PLATFORMS.map(({ key }) => [
        key,
        getSocialMediaFieldName(key, isTeam),
      ]),
    );

    if (type === 'socialMedia') {
      Object.entries(socialMediaFieldMap).forEach(([platform, field]) => {
        const error = validateSocialMediaUrl(
          platform as SocialPlatform,
          data[field],
          t,
        );
        if (error) {
          errors.push([field, { message: error }]);
        }
      });
    }

    if (type === 'teamWebsite') {
      const error = validateWebsiteUrl(data.website_url, t);
      if (error) {
        errors.push(['website_url', { message: error }]);
      }
    }

    if (type === 'teamPhone') {
      const phoneNumber = data.phone_number ?? '';
      if (phoneNumber !== '') {
        const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber);
        if (!parsedPhoneNumber?.isValid()) {
          errors.push(['phone_number', { message: t('Invalid phone number') }]);
        }
      }
    }

    return errors;
  };

  const handleSubmit = async (data: Record<string, any>) => {
    if (!config || !type) {
      return;
    }

    let response;

    if (isTeam) {
      if (type === 'teamPhone') {
        const phoneNumber = data.phone_number ?? '';
        const formattedNumber =
          formatPhoneNumberE164(phoneNumber) ?? phoneNumber;

        // Use the new mutation that only updates primary phone, preserving proxy numbers
        response = await updateTeamPhone({
          variables: {
            team_id: entity.id,
            number: formattedNumber,
          },
        });
      } else {
        const teamUpdateData = formDefinition.reduce((acc, field) => {
          if (field.type === 'address') {
            return {
              ...acc,
              street_number: data.street_number,
              route: data.route,
              postcode: data.postcode,
              locality: data.locality,
              state: data.state,
              country: data.country,
              lat: data.lat,
              lng: data.lng,
            };
          }

          return { ...acc, [field.name]: data[field.name] };
        }, {} as Record<string, unknown>);

        response = await updateTeam({
          variables: {
            id: entity.id,
            team: teamUpdateData,
          },
        });
      }
    } else {
      const userUpdateData = formDefinition.reduce((acc, field) => {
        if (!('name' in field)) {
          return acc;
        }
        return {
          ...acc,
          [field.name]: normalizeUserField(field.name, data[field.name]),
        };
      }, {} as Record<string, unknown>);

      response = await updateUser({
        variables: {
          id: entity.id,
          user: userUpdateData,
        },
      });
    }

    if (response.data == null) {
      return;
    }

    onClose();
  };

  const footerActions = hasOnlyInfoFields ? (
    <Box
      sx={{
        p: 1,
        px: 2,
        gap: 1,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: customPalette.white,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Button variant="contained" onClick={onClose}>
        {t('Confirm')}
      </Button>
    </Box>
  ) : (
    // Use RAForm footer instead
    <></>
  );

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={config?.title ?? ''}
      maxWidth={config?.title === t('Address') ? 'md' : 'sm'}
      footerActions={footerActions}
      loading={loading}
    >
      {infoFields.length > 0 || formDefinition.length > 0 ? (
        <Box>
          {infoFields.map((field, index) =>
            'content' in field ? (
              <Typography
                key={`info-field-${field.content.substring(0, 20)}-${index}`}
                variant="body2"
                component="div"
                sx={{
                  whiteSpace: 'pre-line',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  px: 2,
                  pt: 2,
                  '& > div > p': { m: 0 },
                }}
              >
                <MarkdownParser markdown={field.content} />
              </Typography>
            ) : null,
          )}

          {!hasOnlyInfoFields && config && (
            <Box
              sx={
                infoFields.length > 0
                  ? {
                      '& .MuiGrid-container': { paddingTop: 1 },
                    }
                  : undefined
              }
            >
              <RaForm
                formDefinition={() => formDefinition}
                defaultValues={defaultValues}
                onCancel={onClose}
                onSubmit={handleSubmit}
                validate={validateForm}
                actionButtonsSlotProps={{
                  container: {
                    elevation: 0,
                    sx: theme => ({
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }),
                  },
                }}
              />
            </Box>
          )}
        </Box>
      ) : null}

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />

      {errorMessage && (
        <RASnackbar
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </SlideDialog>
  );
};

export default EntityFieldEditModal;
